import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { CrawlRunErrors } from '../components/CrawlRunErrors';
import { ManualIngestionCell } from '../components/ManualIngestionCell';
import { ManualReviewActions } from '../components/ManualReviewActions';
import { StatusBadge } from '../components/StatusBadge';
import { CRAWL_POLL_MS, isRunStale, type CrawlRun } from '../crawlRuns';
import { formatBytes, formatConfidence, formatCost, formatDate, formatInt, show } from '../format';

// The Device manuals workspace (task manuals_workspace, ADR 0113). Was the
// inbox-only "Manual review" page (ADR 0076): it only ever showed the AI-crawl
// suggestion queue, so once a manual was approved it vanished and there was no
// fleet view of the published library, its parse/index health, parse failures,
// rejected manuals, or device coverage — even though the backend already served
// all of it. This is now the curator's whole workspace:
//
//   • a health strip leading with "Needs attention" (published-but-not-searchable)
//   • a view switcher: Library (published) / Review (suggested) / Needs attention /
//     Rejected — defaulting to Library, the durable state
//   • the published != searchable distinction made first-class: a "Search index"
//     column (ManualIngestionCell) sits beside the review-status column everywhere
//   • crawl operations (trigger + live run strip + recent runs) demoted to the
//     bottom — genuinely live, but secondary to library health
//
// Manual UPLOAD and per-model crawl stay on the device page (an upload must bind
// to one chosen model); every Device cell links there.

type ManualStatus = 'published' | 'suggested' | 'rejected' | 'awaiting_upload';

interface ReviewManual {
  id: string;
  title: string;
  revision: string | null;
  doc_number: string | null;
  is_installation_only: boolean | null;
  language: string | null;
  source: 'upload' | 'crawl';
  source_url: string | null;
  size_bytes: number | null;
  status: ManualStatus;
  confidence: number | null;
  // Why a 'awaiting_upload' row is queued (e.g. 'HTTP 403 (Cloudflare)'), ADR 0116.
  fetch_failure_reason: string | null;
  download_url: string | null;
  device: {
    id: string;
    manufacturer_name: string | null;
    model_id: string | null;
    display_name: string;
  };
}

// The audit-exempt fleet aggregate behind the header + switcher counts + the
// needs-attention worklist (GET /v1/admin/manuals/summary).
interface ManualsSummary {
  statusCounts: { suggested: number; published: number; rejected: number; awaiting_upload: number };
  ingestion: { indexed: number; failed: number; parsing: number; neverIngested: number };
  coverage: { modelsTotal: number; modelsWithPublished: number; modelsWithIndexed: number };
  needsAttentionIds: string[];
}

// One row of the Coverage drill — a device model with no published manual.
interface CoverageDevice {
  id: string;
  display_name: string;
  manufacturer_name: string | null;
  manual_count: number;
}

// Library is the durable state, so it's the default landing view. Coverage is a
// drill reached from the Coverage figure, not a top-level switcher tab.
type View = 'library' | 'review' | 'needs' | 'awaiting' | 'rejected' | 'coverage';

// Which status list each view reads (null = not a manuals list — Coverage reads
// the devices endpoint instead). Library and Needs attention share the published
// list, so toggling between them never issues a second audited read.
function statusForView(view: View): ManualStatus | null {
  switch (view) {
    case 'review':
      return 'suggested';
    case 'awaiting':
      return 'awaiting_upload';
    case 'rejected':
      return 'rejected';
    case 'library':
    case 'needs':
      return 'published';
    case 'coverage':
      return null;
  }
}

export function AdminManuals(): React.ReactElement {
  const [summary, setSummary] = useState<ManualsSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [view, setView] = useState<View>('library');

  // Per-status list cache: each status list is fetched once on first need and
  // reused across view switches. Refresh clears them so they reload fresh.
  const [lists, setLists] = useState<Record<ManualStatus, ReviewManual[] | null>>({
    published: null,
    suggested: null,
    rejected: null,
    awaiting_upload: null,
  });
  const [coverageDevices, setCoverageDevices] = useState<CoverageDevice[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [runs, setRuns] = useState<CrawlRun[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [crawling, setCrawling] = useState(false);
  const [arming, setArming] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Set when POST /crawl returns 503: AI search isn't configured here, so the
  // trigger is disabled and an explanatory card is shown (manual upload on the
  // device page still works).
  const [notConfigured, setNotConfigured] = useState(false);
  const [livePolling, setLivePolling] = useState(false);
  const [expandedErrorsRunId, setExpandedErrorsRunId] = useState<string | null>(null);

  const loadSummary = useCallback(async (): Promise<void> => {
    try {
      const res = await adminApi<ManualsSummary>('/v1/admin/manuals/summary');
      setSummary(res);
      setSummaryError(null);
    } catch (err) {
      const e = err as { body?: { message?: string } };
      setSummaryError(e.body?.message ?? 'Failed to load the manuals summary.');
    }
  }, []);

  const loadList = useCallback(async (status: ManualStatus): Promise<void> => {
    try {
      const res = await adminApi<{ manuals: ReviewManual[] }>(`/v1/admin/manuals?status=${status}`);
      setLists((prev) => ({ ...prev, [status]: res.manuals }));
      setListError(null);
    } catch (err) {
      const e = err as { body?: { message?: string } };
      setListError(e.body?.message ?? 'Failed to load manuals.');
    }
  }, []);

  const loadCoverage = useCallback(async (): Promise<void> => {
    try {
      const res = await adminApi<{ devices: CoverageDevice[] }>('/v1/admin/devices');
      setCoverageDevices(res.devices.filter((d) => d.manual_count === 0));
      setListError(null);
    } catch (err) {
      const e = err as { body?: { message?: string } };
      setListError(e.body?.message ?? 'Failed to load devices.');
    }
  }, []);

  const loadRuns = useCallback(async (): Promise<void> => {
    try {
      const res = await adminApi<{ runs: CrawlRun[] }>('/v1/admin/manuals/crawl-runs');
      setRuns(res.runs.slice(0, 5));
    } catch (err) {
      // Run history is informational — log it so a crawl-runs-specific break is
      // discoverable without blocking the rest of the page.
      console.error('failed to load crawl runs', err);
    }
  }, []);

  useEffect(() => {
    void loadSummary();
    void loadRuns();
  }, [loadSummary, loadRuns]);

  // Lazily load whatever the active view needs (cached after the first load).
  useEffect(() => {
    const status = statusForView(view);
    if (status !== null && lists[status] === null) void loadList(status);
    if (view === 'coverage' && coverageDevices === null) void loadCoverage();
  }, [view, lists, coverageDevices, loadList, loadCoverage]);

  // On-demand refresh: drop every cache and reload the summary + run history; the
  // active-view effect above reloads the now-empty list. Covers the idle case the
  // live timer doesn't — a crawl started elsewhere, or one that finished while the
  // tab was backgrounded — and the post-action count changes.
  const refresh = useCallback(async (): Promise<void> => {
    setRefreshing(true);
    setLists({ published: null, suggested: null, rejected: null, awaiting_upload: null });
    setCoverageDevices(null);
    try {
      await Promise.all([loadSummary(), loadRuns()]);
      setLastRefreshed(new Date().toLocaleTimeString());
    } finally {
      setRefreshing(false);
    }
  }, [loadSummary, loadRuns]);

  // After a review action (approve / reject / delete) the acted-on manual moves
  // between statuses, so any OTHER cached list — and coverage — can now be stale
  // (e.g. approving with Docling unconfigured makes a published manual that's
  // immediately needs-attention, which the Needs view filters from the published
  // list). Optimistically drop the row from the SOURCE list so the active view
  // doesn't flicker to Loading, invalidate every other cache so a moved manual
  // can't linger in or go missing from a stale destination, and refresh the
  // summary so the header figures + switcher counts follow. The active-view effect
  // reloads whatever the current view needs from the now-empty caches.
  const completeAction = useCallback(
    (source: ManualStatus, id: string): void => {
      setLists((prev) => {
        const cur = prev[source];
        return {
          published: null,
          suggested: null,
          rejected: null,
          awaiting_upload: null,
          [source]: cur === null ? null : cur.filter((m) => m.id !== id),
        };
      });
      setCoverageDevices(null);
      void loadSummary();
    },
    [loadSummary]
  );

  // A published manual's ingestion reached a terminal state (a re-ingest or a poll
  // completing) — refresh the summary so a re-indexed manual leaves Needs attention
  // (the worklist filters published rows by summary.needsAttentionIds) and the
  // Indexed / Needs-attention figures stay truthful. Stable identity so the cells'
  // poll intervals don't churn.
  const handleIngestionSettled = useCallback((): void => {
    void loadSummary();
  }, [loadSummary]);

  async function runCrawl(): Promise<void> {
    setCrawling(true);
    setArming(false);
    setActionError(null);
    setNotice(null);
    // Re-evaluate configuration on every attempt: a 503 sets notConfigured, but a
    // retry must be able to clear it (the 503 can be a transient deploy blip).
    setNotConfigured(false);
    try {
      await adminApi<{ accepted: boolean; crawlRunId: string | null }>('/v1/admin/manuals/crawl', {
        method: 'POST',
        body: {},
      });
      setNotice('Crawl started. Suggestions will appear in Review as the run finds them.');
      await loadRuns();
    } catch (err) {
      const e = err as { status?: number; body?: { message?: string } };
      if (e.status === 503) {
        setNotConfigured(true);
      } else {
        setActionError(e.body?.message ?? 'Failed to start the crawl.');
      }
    } finally {
      setCrawling(false);
    }
  }

  const latestRun = runs[0] ?? null;
  const runInProgress = latestRun?.status === 'running';
  const latestRunStartedAt = latestRun?.started_at ?? null;
  const runIsStale = runInProgress && isRunStale(latestRunStartedAt);
  const activeRunInProgress = runInProgress && !runIsStale;
  const triggerDisabled = crawling || activeRunInProgress;

  // While a crawl runs (and isn't a stale zombie), poll ONLY the audit-exempt,
  // cheap crawl-runs status read on a timer. We deliberately do NOT poll the lists
  // or summary here (the list read is audited; polling it each tick would flood the
  // audit log). The full state is pulled once when the run finishes (the transition
  // effect below) and on demand via Refresh.
  useEffect(() => {
    if (!runInProgress || latestRunStartedAt === null) {
      setLivePolling(false);
      return;
    }
    if (isRunStale(latestRunStartedAt)) {
      setLivePolling(false);
      return;
    }
    setLivePolling(true);
    const id = setInterval(() => {
      if (isRunStale(latestRunStartedAt)) {
        clearInterval(id);
        setLivePolling(false);
        return;
      }
      void loadRuns();
    }, CRAWL_POLL_MS);
    return () => clearInterval(id);
  }, [runInProgress, latestRunStartedAt, loadRuns]);

  // When a run leaves 'running' (it finished), the new suggestions land — refresh
  // the summary and invalidate the suggested list so Review reflects them.
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !runInProgress) {
      void loadSummary();
      setLists((prev) => ({ ...prev, suggested: null }));
    }
    wasRunningRef.current = runInProgress;
  }, [runInProgress, loadSummary]);

  const counts = summary
    ? {
        library: summary.statusCounts.published,
        review: summary.statusCounts.suggested,
        needs: summary.needsAttentionIds.length,
        awaiting: summary.statusCounts.awaiting_upload,
        rejected: summary.statusCounts.rejected,
      }
    : null;

  function switchLabel(label: string, n: number | undefined): string {
    return n === undefined ? label : `${label} (${formatInt(n)})`;
  }

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Device manuals</h1>
        {summary !== null ? (
          <span className="admin-count">
            <button
              type="button"
              className="admin-link-button"
              onClick={() => void refresh()}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            {lastRefreshed !== null ? (
              <span className="admin-run-strip-updated">Updated {lastRefreshed}</span>
            ) : null}
          </span>
        ) : null}
      </header>
      <p className="admin-hint">
        NMEA device manuals across every device model: triage AI suggestions, watch the published
        library’s parse/index health, and re-run failed ingestions. To add a manual yourself, open
        its device model — the Device link drops you on its upload form.
      </p>

      {summaryError !== null ? <div className="admin-auth-banner error">{summaryError}</div> : null}

      {/* HEALTH — the AdminSearchUsage totals pattern applied to library health,
          leading with the operationally urgent "Needs attention" figure. */}
      {summary !== null ? (
        <>
          <dl className="admin-defs">
            <dt>Awaiting review</dt>
            <dd>{formatInt(summary.statusCounts.suggested)}</dd>
            <dt>Published</dt>
            <dd>{formatInt(summary.statusCounts.published)}</dd>
            <dt>Indexed (searchable)</dt>
            <dd>{formatInt(summary.ingestion.indexed)}</dd>
            <dt>Coverage</dt>
            <dd>
              {/* Stricter, owner-true coverage: device models with a SEARCHABLE
                  (indexed) manual, not merely a published one. The link drills into
                  the device models with no published manual at all. */}
              <button
                type="button"
                className="admin-link-button"
                onClick={() => setView('coverage')}
              >
                {formatInt(summary.coverage.modelsWithIndexed)} /{' '}
                {formatInt(summary.coverage.modelsTotal)} device models searchable
              </button>
            </dd>
            <dt>Needs attention</dt>
            <dd className="admin-defs-lead">{formatInt(summary.needsAttentionIds.length)}</dd>
          </dl>
          <p className="admin-defs-note">
            “Needs attention” = published manuals that aren’t searchable yet (parse failed or never
            ran). A manual is only searchable by owners once indexing succeeds.
          </p>
        </>
      ) : summaryError === null ? (
        <p className="admin-loading-row">Loading…</p>
      ) : null}

      {/* VIEW SWITCHER — the AdminSearchUsage window-selector primitive (link
          buttons in .admin-count; the active view rendered disabled). */}
      <div className="admin-count admin-seg" role="tablist" aria-label="Device manuals views">
        <button
          type="button"
          className="admin-link-button"
          disabled={view === 'library'}
          onClick={() => setView('library')}
        >
          {switchLabel('Library', counts?.library)}
        </button>
        <button
          type="button"
          className="admin-link-button"
          disabled={view === 'review'}
          onClick={() => setView('review')}
        >
          {switchLabel('Review', counts?.review)}
        </button>
        <button
          type="button"
          className="admin-link-button"
          disabled={view === 'needs'}
          onClick={() => setView('needs')}
        >
          {switchLabel('Needs attention', counts?.needs)}
        </button>
        <button
          type="button"
          className="admin-link-button"
          disabled={view === 'awaiting'}
          onClick={() => setView('awaiting')}
        >
          {switchLabel('Needs manual fetch', counts?.awaiting)}
        </button>
        <button
          type="button"
          className="admin-link-button"
          disabled={view === 'rejected'}
          onClick={() => setView('rejected')}
        >
          {switchLabel('Rejected', counts?.rejected)}
        </button>
      </div>

      {listError !== null ? <div className="admin-auth-banner error">{listError}</div> : null}

      {view === 'library' ? (
        <LibraryView
          manuals={lists.published}
          onDeleted={(id) => completeAction('published', id)}
          onSettled={handleIngestionSettled}
        />
      ) : view === 'review' ? (
        <ReviewView manuals={lists.suggested} onDone={(id) => completeAction('suggested', id)} />
      ) : view === 'needs' ? (
        <NeedsAttentionView
          manuals={lists.published}
          ids={summary?.needsAttentionIds ?? null}
          onSettled={handleIngestionSettled}
        />
      ) : view === 'awaiting' ? (
        <AwaitingView
          manuals={lists.awaiting_upload}
          onResolved={(id) => completeAction('awaiting_upload', id)}
        />
      ) : view === 'rejected' ? (
        <RejectedView manuals={lists.rejected} />
      ) : (
        <CoverageView devices={coverageDevices} />
      )}

      {/* CRAWL OPERATIONS — demoted below library health. Genuinely live while a
          crawl runs, but secondary to the standing library state above. */}
      <div className="admin-crawl-ops">
        <h2>Crawl operations</h2>
        {notConfigured ? (
          <div className="admin-info-card">
            AI manual search isn’t configured in this environment. Set{' '}
            <code>OPENROUTER_API_KEY</code> and a search key (<code>SERPER_API_KEY</code> or{' '}
            <code>TAVILY_API_KEY</code>) to enable it. You can still upload manuals by hand from
            each device model’s page.
          </div>
        ) : null}
        <div className="admin-action-bar">
          {arming ? (
            <span className="admin-confirm">
              <span className="admin-confirm-prompt">
                Search every device model? This runs web-search + LLM calls across the whole fleet.
              </span>
              <button
                type="button"
                className="admin-primary-button admin-button-inline"
                onClick={() => void runCrawl()}
                disabled={crawling || activeRunInProgress}
              >
                {crawling ? 'Starting…' : 'Search the fleet'}
              </button>
              <button
                type="button"
                className="admin-button-secondary"
                onClick={() => setArming(false)}
                disabled={crawling}
              >
                Cancel
              </button>
            </span>
          ) : (
            <>
              <button
                type="button"
                className="admin-primary-button admin-button-inline"
                onClick={() => setArming(true)}
                disabled={triggerDisabled}
              >
                {activeRunInProgress
                  ? 'A crawl is already running'
                  : 'Find manuals across all device models (AI)'}
              </button>
              <p className="admin-hint admin-action-caption">
                Searches every device model for new PDF manuals — costs search + LLM calls. To
                search a single device model instead, use “Find manuals (AI)” on that device model’s
                page.
              </p>
            </>
          )}
        </div>

        {notice !== null ? <p className="admin-hint">{notice}</p> : null}
        {actionError !== null ? <div className="admin-auth-banner error">{actionError}</div> : null}

        {latestRun !== null ? (
          <>
            <div className="admin-run-strip" data-testid="admin-crawl-runs">
              <StatusBadge status={latestRun.status} />
              <span>
                {runIsStale
                  ? `Crawl may be stuck — running for over an hour with no update (${latestRun.models_scanned} scanned, ${latestRun.manuals_suggested} found). Start a new crawl to retry.`
                  : latestRun.status === 'running'
                    ? `Crawl running — ${latestRun.models_scanned} scanned, ${latestRun.manuals_suggested} found so far`
                    : latestRun.status === 'failed'
                      ? `Last crawl failed${latestRun.error !== null ? `: ${latestRun.error}` : ''}`
                      : `Last crawl: ${latestRun.manuals_suggested} suggested across ${latestRun.models_scanned} device models`}
                {latestRun.search_cost_usd !== null
                  ? ` · ~${formatCost(latestRun.search_cost_usd)} web search`
                  : ''}
              </span>
              {livePolling ? <span className="admin-hint">Updating live…</span> : null}
            </div>
            <details className="admin-run-strip-runs">
              <summary>View recent runs</summary>
              <div className="scroll-x">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Scanned</th>
                      <th>Suggested</th>
                      <th>Errors</th>
                      <th>Search cost</th>
                      <th>Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => {
                      const expanded = expandedErrorsRunId === r.id;
                      return (
                        <Fragment key={r.id}>
                          <tr>
                            <td>
                              <StatusBadge status={r.status} />
                            </td>
                            <td>{r.models_scanned}</td>
                            <td>{r.manuals_suggested}</td>
                            <td>
                              {r.error_count > 0 ? (
                                <button
                                  type="button"
                                  className="admin-link-button crawl-error-toggle"
                                  aria-expanded={expanded}
                                  onClick={() =>
                                    setExpandedErrorsRunId((cur) => (cur === r.id ? null : r.id))
                                  }
                                >
                                  <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>{' '}
                                  {r.error_count}
                                </button>
                              ) : (
                                r.error_count
                              )}
                            </td>
                            <td>
                              {r.search_cost_usd !== null
                                ? `~${formatCost(r.search_cost_usd)}`
                                : '—'}
                            </td>
                            <td>{formatDate(r.started_at)}</td>
                          </tr>
                          {expanded ? (
                            <tr className="crawl-error-row">
                              <td colSpan={6}>
                                <CrawlRunErrors runId={r.id} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        ) : null}
      </div>
    </section>
  );
}

// ----- Per-view tables -----------------------------------------------------

// A linked Device cell shared by every view — drops the operator on the device
// model's page (where per-model crawl + upload live).
function DeviceCell({ device }: { device: ReviewManual['device'] }): React.ReactElement {
  return <Link to={`/admin/devices/${device.id}`}>{device.display_name}</Link>;
}

function TitleCell({ manual }: { manual: ReviewManual }): React.ReactElement {
  return (
    <>
      {manual.title}
      {manual.is_installation_only ? ' (installation)' : ''}
    </>
  );
}

function SourcePageCell({ url }: { url: string | null }): React.ReactElement {
  return url !== null && url.length > 0 ? (
    <a href={url} target="_blank" rel="noreferrer">
      Source page
    </a>
  ) : (
    <>—</>
  );
}

// NEEDS MANUAL FETCH — the human-fallback queue (ADR 0116): crawl candidates that
// were 403-blocked (Cloudflare/WAF/IP), so we couldn't download them but a human
// on a residential IP can. Each row hands the admin the exact URL to open by hand;
// the admin uploads the PDF (which resolves the row in place and runs the SAME
// parse/index as an auto-fetch) or dismisses it (the URL won't be suggested again).
function AwaitingView({
  manuals,
  onResolved,
}: {
  manuals: ReviewManual[] | null;
  onResolved: (id: string) => void;
}): React.ReactElement {
  if (manuals === null) return <p className="admin-loading-row">Loading…</p>;
  if (manuals.length === 0) {
    return (
      <div className="admin-empty-state">
        <p className="admin-empty-state-title">Nothing to fetch by hand</p>
        <p className="admin-hint">
          When an AI crawl finds a manual but is blocked (HTTP 403) downloading it, it lands here
          for you to fetch from a browser and upload.
        </p>
      </div>
    );
  }
  return (
    <div className="scroll-x">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>Title</th>
            <th>URL to fetch</th>
            <th>Why blocked</th>
            <th>Upload / Dismiss</th>
          </tr>
        </thead>
        <tbody>
          {manuals.map((m) => (
            <tr key={m.id} data-testid={`admin-awaiting-row-${m.id}`}>
              <td>
                <DeviceCell device={m.device} />
              </td>
              <td>
                <TitleCell manual={m} />
              </td>
              <td>
                {m.source_url !== null && m.source_url.length > 0 ? (
                  <a href={m.source_url} target="_blank" rel="noreferrer">
                    {m.source_url}
                  </a>
                ) : (
                  '—'
                )}
              </td>
              <td>{m.fetch_failure_reason ?? '—'}</td>
              <td>
                <AwaitingActions manual={m} onDone={() => onResolved(m.id)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Per-row Upload (resolve) + Dismiss for the Needs-manual-fetch queue (ADR 0116).
// Upload POSTs the PDF the admin fetched by hand with resolve_manual_id, so the
// server resolves THIS placeholder in place (keeping its crawl provenance) and runs
// the same ingestion as an auto-fetch — it does NOT go through adminApi() because
// that serializes JSON; a manual upload is multipart, like AdminDeviceDetail's.
function AwaitingActions({
  manual,
  onDone,
}: {
  manual: ReviewManual;
  onDone: () => void;
}): React.ReactElement {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(): Promise<void> {
    if (file === null) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('resolve_manual_id', manual.id);
      // Keep the crawl-derived title; the server defaults to the filename otherwise.
      if (manual.title.length > 0) form.append('title', manual.title);
      if (manual.source_url !== null && manual.source_url.length > 0) {
        form.append('source_url', manual.source_url);
      }
      form.append('file', file);
      const res = await fetch(`/v1/admin/devices/${manual.device.id}/manuals`, {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });
      if (!res.ok) {
        let message = 'Failed to upload the manual.';
        try {
          const body = (await res.json()) as { message?: string };
          if (typeof body.message === 'string' && body.message.length > 0) message = body.message;
        } catch {
          // non-JSON error body — keep the default message.
        }
        throw new Error(message);
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload the manual.');
    } finally {
      setBusy(false);
    }
  }

  async function dismiss(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await adminApi(`/v1/admin/manuals/${manual.id}/dismiss`, { method: 'POST' });
      onDone();
    } catch (err) {
      const apiMessage = (err as { body?: { message?: string } }).body?.message;
      setError(apiMessage ?? (err instanceof Error ? err.message : 'Failed to dismiss.'));
    } finally {
      setBusy(false);
    }
  }

  if (dismissing) {
    return (
      <span className="admin-confirm">
        <span className="admin-confirm-prompt">
          Dismiss this candidate? Its URL won’t be suggested again.
        </span>
        <button
          type="button"
          className="admin-button-danger"
          onClick={() => void dismiss()}
          disabled={busy}
        >
          {busy ? 'Working…' : 'Dismiss'}
        </button>
        <button
          type="button"
          className="admin-button-secondary"
          onClick={() => {
            setError(null);
            setDismissing(false);
          }}
          disabled={busy}
        >
          Cancel
        </button>
        {error !== null ? <span className="admin-inline-error">{error}</span> : null}
      </span>
    );
  }

  return (
    <span className="admin-confirm">
      <input
        type="file"
        accept="application/pdf"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        disabled={busy}
        aria-label={`Upload PDF for ${manual.title}`}
      />
      <button
        type="button"
        className="admin-button-affirm"
        onClick={() => void upload()}
        disabled={busy || file === null}
      >
        {busy ? 'Uploading…' : 'Upload PDF'}
      </button>
      <button
        type="button"
        className="admin-button-danger"
        onClick={() => setDismissing(true)}
        disabled={busy}
      >
        Dismiss
      </button>
      {error !== null ? <span className="admin-inline-error">{error}</span> : null}
    </span>
  );
}

// LIBRARY — every published manual fleet-wide, with the review-status column and
// the SEPARATE "Search index" column (ManualIngestionCell) that closes the
// published-≠-searchable blind spot. Delete is the only destructive action here.
function LibraryView({
  manuals,
  onDeleted,
  onSettled,
}: {
  manuals: ReviewManual[] | null;
  onDeleted: (id: string) => void;
  onSettled: () => void;
}): React.ReactElement {
  if (manuals === null) return <p className="admin-loading-row">Loading…</p>;
  if (manuals.length === 0) {
    return (
      <div className="admin-empty-state">
        <p className="admin-empty-state-title">No published manuals yet</p>
        <p className="admin-hint">
          Approve an AI suggestion in Review, or upload a PDF from a device model’s page.
        </p>
      </div>
    );
  }
  return (
    <div className="scroll-x">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>Title</th>
            <th>Revision</th>
            <th>Status</th>
            <th>Search index</th>
            <th>Size</th>
            <th>File</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {manuals.map((m) => (
            <tr key={m.id} data-testid={`admin-library-row-${m.id}`}>
              <td>
                <DeviceCell device={m.device} />
              </td>
              <td>
                <TitleCell manual={m} />
              </td>
              <td>{show(m.revision)}</td>
              <td>
                <StatusBadge status={m.status} />
              </td>
              <td>
                <ManualIngestionCell
                  manualId={m.id}
                  published={m.status === 'published'}
                  onSettled={onSettled}
                />
              </td>
              <td>{formatBytes(m.size_bytes)}</td>
              <td>
                {m.download_url !== null ? (
                  <a href={m.download_url} target="_blank" rel="noreferrer">
                    Download
                  </a>
                ) : (
                  '—'
                )}
              </td>
              <td>
                <ManualReviewActions
                  manualId={m.id}
                  deviceModelId={m.device.id}
                  actions={['delete']}
                  onDone={() => onDeleted(m.id)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// REVIEW — the AI suggestion inbox: the daily triage loop, unchanged so it doesn't
// regress. Preview each PDF, approve (publish to every owner) or reject (delete
// the file; the source URL is never suggested again).
function ReviewView({
  manuals,
  onDone,
}: {
  manuals: ReviewManual[] | null;
  onDone: (id: string) => void;
}): React.ReactElement {
  if (manuals === null) return <p className="admin-loading-row">Loading…</p>;
  if (manuals.length === 0) {
    return (
      <div className="admin-empty-state">
        <p className="admin-empty-state-title">Inbox zero</p>
        <p className="admin-hint">
          No AI suggestions awaiting review. Start a crawl below to look for new manuals, or upload
          one directly from a device model’s page.
        </p>
      </div>
    );
  }
  return (
    <div className="scroll-x">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>Title</th>
            <th>Revision</th>
            <th title="The AI judge’s confidence this PDF is the right manual for this device model">
              Confidence
            </th>
            <th>Size</th>
            <th>Source page</th>
            <th>Preview</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {manuals.map((m) => (
            <tr key={m.id} data-testid={`admin-review-row-${m.id}`}>
              <td>
                <DeviceCell device={m.device} />
              </td>
              <td>
                <TitleCell manual={m} />
              </td>
              <td>{show(m.revision)}</td>
              <td>{formatConfidence(m.confidence)}</td>
              <td>{formatBytes(m.size_bytes)}</td>
              <td>
                <SourcePageCell url={m.source_url} />
              </td>
              <td>
                {m.download_url !== null ? (
                  // No-audit by design: the server skips the audit row for a
                  // 'suggested' manual (ADR 0079), so triaging many previews
                  // doesn't write one per PDF opened.
                  <a href={m.download_url} target="_blank" rel="noreferrer">
                    Preview PDF
                  </a>
                ) : (
                  '—'
                )}
              </td>
              <td>
                <ManualReviewActions
                  manualId={m.id}
                  actions={['approve', 'reject']}
                  onDone={() => onDone(m.id)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// NEEDS ATTENTION — the re-ingest worklist: published manuals that aren't
// searchable (parse failed or never ran). The id set is server-authoritative
// (from /summary), so the worklist always matches the header figure and needs no
// per-row discovery scan. Re-ingest is inline (ManualIngestionCell).
function NeedsAttentionView({
  manuals,
  ids,
  onSettled,
}: {
  manuals: ReviewManual[] | null;
  ids: string[] | null;
  onSettled: () => void;
}): React.ReactElement {
  if (manuals === null || ids === null) return <p className="admin-loading-row">Loading…</p>;
  const idSet = new Set(ids);
  const rows = manuals.filter((m) => idSet.has(m.id));
  if (rows.length === 0) {
    return (
      <div className="admin-empty-state">
        <p className="admin-empty-state-title">Nothing needs attention</p>
        <p className="admin-hint">Every published manual is indexed and searchable by owners.</p>
      </div>
    );
  }
  return (
    <div className="scroll-x">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>Title</th>
            <th>Revision</th>
            <th>Search index</th>
            <th>File</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id} data-testid={`admin-needs-row-${m.id}`}>
              <td>
                <DeviceCell device={m.device} />
              </td>
              <td>
                <TitleCell manual={m} />
              </td>
              <td>{show(m.revision)}</td>
              <td>
                <ManualIngestionCell
                  manualId={m.id}
                  published={m.status === 'published'}
                  onSettled={onSettled}
                />
              </td>
              <td>
                {m.download_url !== null ? (
                  <a href={m.download_url} target="_blank" rel="noreferrer">
                    Download
                  </a>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// REJECTED — a read-only audit rear-view of discards (bytes already deleted, so no
// preview). Lets an operator sanity-check an over-aggressive reject and see what
// the crawl keeps surfacing wrongly.
function RejectedView({ manuals }: { manuals: ReviewManual[] | null }): React.ReactElement {
  if (manuals === null) return <p className="admin-loading-row">Loading…</p>;
  if (manuals.length === 0) {
    return (
      <div className="admin-empty-state">
        <p className="admin-empty-state-title">Nothing rejected</p>
        <p className="admin-hint">Rejected AI suggestions will appear here for audit.</p>
      </div>
    );
  }
  return (
    <div className="scroll-x">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>Title</th>
            <th>Revision</th>
            <th>Confidence</th>
            <th>Source page</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {manuals.map((m) => (
            <tr key={m.id} data-testid={`admin-rejected-row-${m.id}`}>
              <td>
                <DeviceCell device={m.device} />
              </td>
              <td>
                <TitleCell manual={m} />
              </td>
              <td>{show(m.revision)}</td>
              <td>{formatConfidence(m.confidence)}</td>
              <td>
                <SourcePageCell url={m.source_url} />
              </td>
              <td>
                <StatusBadge status={m.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// COVERAGE DRILL — device models with no published manual at all, each linking to
// its page (where per-model crawl + upload live). Models that DO have a published
// manual but it isn't indexed yet show up in Needs attention instead, not here.
function CoverageView({ devices }: { devices: CoverageDevice[] | null }): React.ReactElement {
  if (devices === null) return <p className="admin-loading-row">Loading…</p>;
  if (devices.length === 0) {
    return (
      <div className="admin-empty-state">
        <p className="admin-empty-state-title">Every device model has a published manual</p>
        <p className="admin-hint">
          Device models with a published-but-unindexed manual are in Needs attention.
        </p>
      </div>
    );
  }
  return (
    <>
      <p className="admin-hint">
        Device models with no published manual at all. (A device model with a
        published-but-unindexed manual is in Needs attention, not here.)
      </p>
      <div className="scroll-x">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Device model</th>
              <th>Manufacturer</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id} data-testid={`admin-coverage-row-${d.id}`}>
                <td>
                  <Link to={`/admin/devices/${d.id}#manuals`}>{d.display_name}</Link>
                </td>
                <td>{show(d.manufacturer_name)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
